import { Box, Heading, Link, Text, Button, useToast, Code } from "@chakra-ui/react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import { Components } from "react-markdown";
import React from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { coldarkDark } from "react-syntax-highlighter/dist/esm/styles/prism";

interface ConversationMarkdownProps {
  content: string;
}

const ConversationMarkdown = React.memo(({ content }: ConversationMarkdownProps) => {
  const toast = useToast();

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code)
      .then(() => {
        toast({
          title: "Code copied!",
          status: "success",
          duration: 2500,
          isClosable: true,
        });
      })
      .catch((err) => {
        console.error("Failed to copy code: ", err);
        toast({
          title: "Failed to copy code",
          status: "error",
          duration: 2500,
          isClosable: true,
        });
      });
  }

  const markdownComponents: Components = {
    h1: ({ ...props }) => <Heading as="h1" size="xl" my={4} {...props} />,
    h2: ({ ...props }) => <Heading as="h2" size="lg" my={4} {...props} />,
    h3: ({ ...props }) => <Heading as="h3" size="md" my={3} {...props} />,
    h4: ({ ...props }) => <Heading as="h4" size="sm" my={2} {...props} />,
    h5: ({ ...props }) => <Heading as="h5" size="xs" my={2} {...props} />,
    h6: ({ ...props }) => <Heading as="h6" size="xs" my={1} {...props} />,
    p: ({ ...props }) => <Text my={2} {...props} />,
    ul: ({ ...props }) => <Box as="ul" pl={4} my={2} {...props} />,
    ol: ({ ...props }) => <Box as="ol" pl={4} my={2} {...props} />,
    li: ({ ...props }) => <Box as="li" my={1} {...props} />,
    blockquote: ({ ...props }) => (
      <Box as="blockquote" p={2} my={2} bg="gray.100" borderRadius={5} {...props} />
    ),
    code: ({ className, children, ...props }) => {
      const match = /language-(\w+)/.exec(className || '');
      const codeContent = String(children).replace(/\n$/, '');
      const isInline = !className;
      return isInline ? (
        <Code as="code" p={1} bg="gray.100" borderRadius={5} {...props}>
          {children}
        </Code>
      ) : (
        <Box position="relative" my={2} borderRadius={5} overflow="hidden">
          <Button
            size="sm"
            position="absolute"
            top={4}
            right={2}
            onClick={() => handleCopyCode(codeContent)}
            zIndex={1}
            _hover={{ bg: "#ecc94b" }}
          >
            Copy
          </Button>
          <SyntaxHighlighter
            style={coldarkDark}
            language={match ? match[1] : 'text'}
            PreTag="div"
            {...props}
          >
            {codeContent}
          </SyntaxHighlighter>
        </Box>
      );
    },
    a: ({ ...props }) => <Link as="a" color="blue.500" {...props} />,
    img: ({ ...props }) => <Box as="img" my={2} {...props} />,
    table: ({ ...props }) => <Box as="table" my={2} {...props} />,
    thead: ({ ...props }) => <Box as="thead" {...props} />,
    tbody: ({ ...props }) => <Box as="tbody" {...props} />,
    tr: ({ ...props }) => <Box as="tr" {...props} />,
    th: ({ ...props }) => <Box as="th" p={2} bg="gray.100" {...props} />,
    td: ({ ...props }) => <Box as="td" p={2} {...props} />,
  };

  return (
    <Markdown
      remarkPlugins={[remarkGfm, remarkMath]}
      rehypePlugins={[rehypeKatex]}
      components={markdownComponents}
    >
      {content}
    </Markdown>
  );
})

export default ConversationMarkdown;
